import React, { useState, useRef, useEffect } from "react";
import { IonModal, IonIcon, IonButton, IonInput } from "@ionic/react";
import { close, checkmark, add } from "ionicons/icons";

interface Option {
  id: string;
  label: string;
  selected: boolean;
}

interface SelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  buttonText?: string;
  options: Option[];
  onSave: (e: any, fieldName: string) => void;
  onRemove?: () => void;
  multiSelect?: boolean;
  fieldName: string;
  allowCustomOptions?: boolean;
  showSearch?: boolean;
  /** ✅ New prop to control whether selected items appear on top */
  selectedOnTop?: boolean;
}

const SelectionModal: React.FC<SelectionModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  buttonText = "Save",
  options: initialOptions,
  onSave,
  onRemove,
  multiSelect = false,
  fieldName,
  allowCustomOptions = false,
  showSearch = false,
  selectedOnTop = true, // ✅ Default behavior
}) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [newOptionText, setNewOptionText] = useState<string>("");
  const [showAddOption, setShowAddOption] = useState<boolean>(false);
  const newOptionInputRef = useRef<HTMLIonInputElement>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  useEffect(() => {
    const mappedOptions = initialOptions.map((option) => ({
      ...option,
      selected: option.selected || false,
    }));
    setOptions(mappedOptions);
    setSelectedOptions(mappedOptions.filter((option) => option.selected));
  }, [initialOptions]);

  const handleOptionClick = (clickedOption: Option) => {
    setSelectedOptions((prevSelected) => {
      if (multiSelect) {
        setOptions(
          options.map((option) =>
            option.id === clickedOption.id
              ? { ...option, selected: !option.selected }
              : option
          )
        );
        const isSelected = prevSelected.some(
          (opt) => opt.id === clickedOption.id
        );
        return isSelected
          ? prevSelected.filter((opt) => opt.id !== clickedOption.id)
          : [...prevSelected, clickedOption];
      } else {
        setOptions(
          options.map((option) => ({
            ...option,
            selected: option.id === clickedOption.id,
          }))
        );
        return [clickedOption];
      }
    });
  };

  const addNewOption = (value: string) => {
    if (value.trim() === "") return;
    const customOptionsCount = options.filter(
      (opt) => !initialOptions.some((initOpt) => initOpt.id === opt.id)
    ).length;
    if (customOptionsCount >= 4) return;

    const newOption: Option = {
      id: value.trim(),
      label: value.trim(),
      selected: true,
    };

    setOptions([...options, newOption]);
    setNewOptionText("");
    setShowAddOption(false);
  };

  const handleNewOptionChange = (e: any) => {
    setNewOptionText(e.detail.value || "");
  };

  const toggleAddOption = () => {
    setShowAddOption(!showAddOption);
    if (!showAddOption) {
      setTimeout(() => {
        newOptionInputRef.current?.setFocus();
      }, 100);
    }
  };

  const handleSave = () => {
    const selected = options.filter((option) => option.selected);
    if (multiSelect) {
      const selectedValues = selectedOptions.map((option) => option.id);
      const customEvent = {
        detail: {
          value: selectedValues.join("|"),
        },
      };
      onSave(customEvent, fieldName);
    } else {
      const selectedValue =
        selectedOptions.length > 0 ? selectedOptions[0].id : "";
      const customEvent = {
        detail: {
          value: selectedValue,
        },
      };
      onSave(customEvent, fieldName);
    }
    onClose();
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="container-modal"
      style={{
        "--border-radius": "30px",
        "--width": "362px",
        "--height": "auto",
      }}
    >
      <div className="flex flex-col p-6 h-full overflow-auto">
        <div className="w-full flex justify-end mb-2">
          <button onClick={onClose} className="text-gray-400">
            <IonIcon icon={close} style={{ fontSize: "24px" }} />
          </button>
        </div>

        <h2 className="text-3xl font-bold text-center mb-4">{title}</h2>
        {showAddOption && (
          <h3 className="text-l font-semibold text-center mb-2">
            Add a custom option
          </h3>
        )}
        {subtitle && <p className="text-center mb-4">{subtitle}</p>}

        {showSearch && (
          <div className="w-full mb-4">
            <IonInput
              value={newOptionText}
              onIonInput={(e: any) => setNewOptionText(e.target.value || "")}
              placeholder="Search options..."
              className="custom-input w-full"
              clearInput
            />
          </div>
        )}

        <div
          className="w-full mb-6"
          style={{
            maxHeight: showSearch ? "250px" : "300px",

            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {!showAddOption &&
            options
              .filter((option: Option) =>
                option.label.toLowerCase().includes(newOptionText.toLowerCase())
              )
              /** ✅ Conditionally sort based on selectedOnTop prop */
              .sort((a, b) =>
                selectedOnTop ? Number(b.selected) - Number(a.selected) : 0
              )
              .map((option: Option) => (
                <div
                  key={option.id}
                  className="flex items-center justify-between py-4 border-b"
                  onClick={() => handleOptionClick(option)}
                >
                  <span className="text-lg">{option.label}</span>
                  {selectedOptions.some((opt) => opt.id === option.id) && (
                    <div className="bg-gray-400 rounded-full p-1 flex items-center justify-center w-6 h-6">
                      <IonIcon icon={checkmark} style={{ color: "white" }} />
                    </div>
                  )}
                </div>
              ))}

          {multiSelect && allowCustomOptions && (
            <div className="mt-4">
              {!showAddOption ? (
                <div
                  className="flex items-center text-blue-500 py-4 cursor-pointer"
                  onClick={toggleAddOption}
                >
                  <IonIcon icon={add} style={{ marginRight: "8px" }} />
                  <span>Add custom option</span>
                </div>
              ) : (
                <div className="flex items-center py-4">
                  <IonInput
                    style={{ paddingBottom: "12px" }}
                    ref={newOptionInputRef}
                    value={newOptionText}
                    onIonChange={handleNewOptionChange}
                    onBlur={(e) => addNewOption(e.target.value as string)}
                    placeholder="Enter new option"
                    className="custom-input flex-grow"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="w-full m-auto  display-flex align-items-center flex-column"
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <IonButton
            expand="block"
            onClick={
              showAddOption
                ? () => {
                    setShowAddOption(false);
                    addNewOption(newOptionText);
                  }
                : handleSave
            }
            className="primary-button w-full mb-4"
            style={{
              "--border-radius": "30px",
              fontSize: "18px",
              fontWeight: "500",
            }}
          >
            {showAddOption ? "Add Option" : `${buttonText}`}
          </IonButton>

          {onRemove && (
            <div
              onClick={() => {
                onRemove();
                setSelectedOptions([]);
                setOptions(
                  options.map((option) => ({ ...option, selected: false }))
                );
              }}
              className="text-center text-red-500 font-medium p-2 m-2 text-lg"
            >
              Remove {title}
            </div>
          )}
        </div>
      </div>
    </IonModal>
  );
};

export default SelectionModal;
